/**
 * The single decimal parser/canonicalizer for form inputs (forms standard
 * §3.6). pt-BR keyboards emit commas, so both "," and "." are accepted as the
 * decimal separator. Empty/invalid input is `undefined`, never `0` — the
 * `"" → 0` coercion of `z.coerce.number()` / `parseFloat` fallbacks is the
 * exact bug this module retires, which is also why `z.coerce` is banned in
 * form schemas.
 */

const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;

/** Normalizes a raw input string: trims and unifies the decimal separator. */
function normalize(input: string): string {
	return input.trim().replace(",", ".");
}

/**
 * Parses a user-typed decimal string. Accepts comma or dot as separator.
 * Returns `undefined` for empty or non-numeric input — never `0`.
 */
export function parseDecimalInput(input: string): number | undefined {
	const normalized = normalize(input);
	if (!DECIMAL_PATTERN.test(normalized)) return undefined;
	const value = Number.parseFloat(normalized);
	return Number.isFinite(value) ? value : undefined;
}

/**
 * Parses a money input string into integer cents using exact string math (no
 * float arithmetic — money sums/compares are always done in cents, §3.6).
 * Fractions beyond 2 digits round half-up on the third digit.
 * Returns `undefined` for empty or non-numeric input.
 */
export function toCents(input: string): number | undefined {
	const normalized = normalize(input);
	if (!DECIMAL_PATTERN.test(normalized)) return undefined;
	const negative = normalized.startsWith("-");
	const unsigned = negative ? normalized.slice(1) : normalized;
	const [intPart, fracPart = ""] = unsigned.split(".");
	const frac = fracPart.padEnd(3, "0");
	let cents =
		Number.parseInt(intPart, 10) * 100 + Number.parseInt(frac.slice(0, 2), 10);
	if (Number.parseInt(frac[2] ?? "0", 10) >= 5) cents += 1;
	return negative ? -cents : cents;
}

/**
 * Formats integer cents as the canonical API decimal string ("1234.50" —
 * always dot separator, always 2 fraction digits), matching the shared-types
 * money-as-decimal-string wire contract.
 */
export function centsToDecimalString(cents: number): string {
	const negative = cents < 0;
	const unsigned = Math.abs(Math.round(cents));
	const intPart = Math.floor(unsigned / 100);
	const fracPart = String(unsigned % 100).padStart(2, "0");
	return `${negative ? "-" : ""}${intPart}.${fracPart}`;
}
